/**
 * Hooks de la configuración global (TanStack Query).
 *
 * Clave de la caché: ['configuracion']. Cambia muy poco, así que se considera fresca 5 minutos.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  etiquetasPrioridadPorDefecto,
  type Configuracion,
  type EtiquetasPrioridad,
  type Prioridad,
} from '@cdtec-backlog/contrato';
import { guardarPrioridades, obtenerConfiguracion } from './api';

const CINCO_MINUTOS_MS = 5 * 60_000;

/**
 * Nombre visible de una prioridad. Mientras la configuración carga (o si falla) usa los nombres
 * por defecto del contrato, para que no haya parpadeo ni se vea «P0» crudo.
 */
export function etiquetaPrioridad(config: Configuracion | undefined, p: Prioridad): string {
  return (config?.prioridades ?? etiquetasPrioridadPorDefecto())[p];
}

export function useConfiguracion() {
  return useQuery({
    queryKey: ['configuracion'],
    queryFn: obtenerConfiguracion,
    staleTime: CINCO_MINUTOS_MS,
  });
}

/** Función `(p) => nombre` lista para usar en componentes. */
export function useEtiquetaPrioridad(): (p: Prioridad) => string {
  const { data } = useConfiguracion();
  return (p) => etiquetaPrioridad(data, p);
}

export function useGuardarPrioridades() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (etiquetas: EtiquetasPrioridad) => guardarPrioridades(etiquetas),
    onSuccess: (prioridades) => {
      cliente.setQueryData<Configuracion>(['configuracion'], { prioridades });
      void cliente.invalidateQueries({ queryKey: ['configuracion'] });
    },
  });
}
